// listen — play each option (neutral ▶ A / ▶ B buttons), then commit to an
// impression chip BEFORE any reveal: the chips answer the prompt itself
// (moment.choices, e.g. "A" / "B" / "honestly, can't tell yet"), and only a
// pick opens that choice's response. Impressions, never answers: no pick is
// ever wrong, re-picking is always allowed, and any pick counts as engaged —
// the escape chip's listening hint is a bonus nudge, not a gate. Continue
// unlocks after at least one listen and one pick.

import React, { useCallback, useEffect, useRef, useState } from "react";
import { PATCHES, playPhrase, resumeAudio } from "../../../lib/homeKeyAudio";

export default function ListenMoment({ moment, onReadyChange }) {
  const options = moment.options || [];
  // Old-shaped moments have no `choices`; fall back to one chip per option so
  // the component keeps working on legacy data.
  const choices =
    moment.choices && moment.choices.length
      ? moment.choices
      : options.map((option) => ({ id: option.id, text: option.label }));
  const [listened, setListened] = useState(() => new Set());
  const [playingId, setPlayingId] = useState(null);
  const [picked, setPicked] = useState(null);
  const playRef = useRef(null);
  const timerRef = useRef(null);

  const stopAll = useCallback(() => {
    if (playRef.current && playRef.current.stop) playRef.current.stop();
    playRef.current = null;
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = null;
    setPlayingId(null);
  }, []);
  useEffect(() => () => stopAll(), [stopAll]);

  const ready = listened.size > 0 && picked != null;
  useEffect(() => {
    if (onReadyChange) onReadyChange(ready);
  }, [ready, onReadyChange]);

  const play = (option) => {
    resumeAudio();
    stopAll();
    const handle = playPhrase(option.phrase, PATCHES.softKeys);
    playRef.current = handle && handle.stop ? handle : null;
    setPlayingId(option.id);
    setListened((s) => {
      if (s.has(option.id)) return s;
      const n = new Set(s);
      n.add(option.id);
      return n;
    });
    timerRef.current = setTimeout(
      () => setPlayingId(null),
      ((handle && handle.durMs) || 3000) + 200
    );
  };

  return (
    <div className="hk-moment-body">
      <p>{moment.prompt}</p>

      <div className="hk-choice-row">
        {options.map((option, i) => (
          <button
            key={option.id}
            type="button"
            className={`hk-demo-btn${playingId === option.id ? " hk-demo-btn--on" : ""}`}
            onClick={() => play(option)}
          >
            ▶ {option.label || String.fromCharCode(65 + i)}
          </button>
        ))}
      </div>

      <div className="hk-choice-row" role="group" aria-label="what did it feel like?">
        {choices.map((choice) => (
          <button
            key={choice.id}
            type="button"
            className={`hk-choice${picked === choice.id ? " hk-choice--picked" : ""}`}
            aria-pressed={picked === choice.id}
            onClick={() => setPicked(choice.id)}
          >
            {choice.text}
          </button>
        ))}
      </div>

      {picked != null && moment.responses && moment.responses[picked] && (
        <p className="hk-listen-response">{moment.responses[picked]}</p>
      )}
      {moment.note && <p className="hk-listen-note">{moment.note}</p>}
    </div>
  );
}
